import { ThemeConfig } from './themeConfig';
import { translate } from '../utils/translations';
import { SiteLanguage } from 'constants/locale';
import keywordsUS from './keywordsUS';

export const EXP_MENU_LINK_LABEL_SIGN_IN = 'Sign In' as const;
export const EXP_MENU_LINK_LABEL_MY_SEARCHES = 'My Searches' as const;

const ExprealtyUsTheme: ThemeConfig = {
  name: 'eXp Realty',
  title: 'eXp Realty®',
  faviconPath: 'favicon-exp.ico',
  metaDescription: 'Largest residential real estate brokerage in North America. Where the pros go to grow.',
  largeDesktopHeaderImage: '/next/assets/images/header-image-large-desktop-exp-ca.jpg',
  desktopHeaderImage: '/next/assets/images/header-image-desktop-exp-ca.jpg',
  mobileHeaderImage: '/next/assets/images/header-image-mobile-exp-ca.jpg',
  smallMobileImage: '/next/assets/images/header-image-small-mobile-exp-ca.jpg',
  ogSiteName: 'eXp Realty®',
  ogImage: 'https://www.exprealty.com/next/assets/images/content-cards/us-buy.jpg',
  ogImageAlt: 'A couple stands arm in arm, facing a modern building with large windows',
  schemaUrl: 'https://www.exprealty.com',
  schemaLogo: 'https://exprealty.ca/next/assets/images/eXp-Realty-logo.png',
  schemaLocation: {
    addressCountry: 'United States',
    addressLocality: 'Bellingham',
    addressRegion: 'WA',
    postalCode: '98226',
    streetAddress: '2219 Rimland Drive Suite 301',
  },
  schemaFoundingDate: '2002',
  schemaFounders: [
    {
      '@type': 'Person',
      name: 'Sanford Glenn Darrel',
    },
    {
      '@type': 'Person',
      name: 'Brian Culhane',
    },
  ],
  colors: {
    primaryVeryLight: '#8ECFFF',
    primaryLight:'#E8EDF5',
    primary: '#0C0F24',
    primaryDark: '#0C0F24',
    primaryVeryDark: '#0C0F24',
    primaryBackground: '#FFF',
    primaryForSale: '#FFF',
    primarySold: '#6D071A',
    primarySoldFocus: '#6D071A',
    primaryFilters: '#0C0F24',
    primaryFontColor: '#0C0F24',
    primaryLinkColor: '#1B489B',
    primaryFocusLinkColor: '#1B489B',
    primaryInvertedBackground: '#FFFFFF',
    primaryBorderColor: '#5A5A5A',
    primaryFocusColor: '#F3F4F4',
    primaryFocusBorderColor: '#0C0F24',
    primaryChart: '#F3F3F4',
    secondaryBackground: '#FFF',
    secondaryBorderColor: '#5A5A5A',
    secondaryLinkColor: '#3D3F50',
    secondaryHoverColor: '#0C0F24',
    secondaryFocusColor: '#3D3F50',
    secondaryFocusBorderColor: '#5A5A5A',
    tertiaryBackground: '#EFEFEF',
    tertiaryBackgroundLight: '#3D3F50',
    tertiaryBorderColor: '#5A5A5A',
    tertiaryLinkColor: '#1B489B',
    quaternaryLinkColor: '#496DAF',
    quaternaryBorderColor: '#5A5A5A',
    quaternaryBackgroundLight: '#FFFFFF',
    transparentBackground: 'rgba(255, 255, 255, 0.1)',
    mapPinForSale: '#1B489B',
    mapPinForSaleFocus: '#1B489B',
    mapControlBackground: '#FFF',
    mapControlBackgroundHover: '#F3F4F4',
    mapControlBackgroundActive: '#E7E7E9',
    carouselArrowHover: '#F3F4F4',
    carouselArrowFocus: '#CECFD3',
    notFound404PinHeadColor: '#1B489B',
    notFOund404PinBodyColor: '#496DAF',
  },
  fonts: {
    heading: 'Manrope',
    text: 'Manrope',
    headingWeight: '300',
    headingBoldWeight: '600',
  },
  decorations: {
    brushstrokes: false,
    boxShadow: '0px 2px 4px 0px #00000014',
    boxShadowLight: '0 0 8px 0 rgba(0, 0, 0, .1)',
    areaPageLineWidth: '51px',
  },
  buttons: {
    borderRadius: '100px',
    borderRadiusSmall: '8px',
    borderRadiusMedium: '8px',
    borderRadiusLarge: '100px',
    borderRadiusModal: '12px',
    borderRadiusBanner: '12px',
  },
  style_overrides: {
    breadcrumbs: {
      mobileMarginTop: '1.5em',
      boxShadow: '0px 2px 4px 0px #00000014',
    },
    areaListingsPage: {
      pageTitleFontWeight: 'bold',
    },
    locationUnavailable: {
      imagePath: '/next/assets/images/listing-page/location-unavailable-exp.svg',
    },
  },
  icons: {
    borderRadius: '100px',
  },
  organization: {
    phoneNumber() {
      return '1 833-303-0610';
    },
    email() {
      return 'info@exprealty.net';
    },
    address() {
      return '2219 Rimland Drive, Suite 301,\nBellingham, Washington, 98226';
    },
    twitterUrl: 'https://x.com/exprealty',
    facebookUrl: 'https://www.facebook.com/eXpRealty/',
    instagramUrl: 'https://www.instagram.com/exprealty',
    linkedinUrl: 'https://www.linkedin.com/company/exp-realty',
    youtubeUrl: 'https://www.youtube.com/@eXpRealty',
    joinSectionUrl: '/join-exp',
  },
  menuLinks: [
    {
      id: 'my-searches',
      label: EXP_MENU_LINK_LABEL_MY_SEARCHES,
      link: '/account/saved/searches',
    },
    {
      id: 'buy',
      label: 'Buy',
      link: '/search?openSearch=true',
    },
    {
      id: 'sell',
      label: 'Sell',
      link: '/sell',
    },
    {
      id: 'find-an-agent',
      label: 'Find An Agent',
      link: '/agents-search',
    },
    {
      id: 'join-exp',
      label: 'Join eXp',
      link: 'https://join.exprealty.com',
      newTab: true,
    },
    {
      id: 'agent-login',
      label: 'Agent Login',
      link: 'https://my.exprealty.com/login',
      newTab: true,
    },
    {
      id: 'about',
      label: 'About',
      link: '/explore-exp',
    },
    {
      id: 'accessibility',
      label: 'Accessibility',
      link: '/accessibility',
    },
    {
      id: 'sitemap',
      label: 'Sitemap',
      link: '/sitemap',
    },
  ],
  footerContent: {
    column1: [
      {
        label: 'Explore eXp',
        link: 'https://www.exprealty.com',
        isAnchor: true,
        target: '_blank',
      },
      {
        label: 'Find an Agent',
        link: '/agents-search',
        isAnchor: true,
        target: '_blank',
      },
      {
        label: 'Join eXp',
        link: 'https://join.exprealty.com',
        isAnchor: true,
        target: '_blank',
      },
      {
        label: 'Investor Relations',
        link: 'https://expworldholdings.com/',
        target: '_blank',
      },
      {
        label: 'Company Careers',
        link: 'https://expworldholdings.com/careers/',
        target: '_blank',
      },
      {
        label: 'Home Guides',
        link: 'https://exprealty.com/guides/',
        isAnchor: true,
        target: '_blank',
      },
    ],
    column2: [
      {
        label: 'eXp Life',
        link: 'https://life.exprealty.com/',
        target: '_blank',
      },
      {
        label: 'Zoocasa',
        link: 'https://zoocasa.com/',
        target: '_blank',
      },
      {
        label: 'SUCCESS',
        link: 'https://www.success.com/',
        target: '_blank',
      },
      {
        label: 'eXp Revenos',
        link: 'https://revenos.com/',
        target: '_blank',
      },
      {
        label: 'eXp Commercial',
        link: 'https://expcommercial.com/',
        target: '_blank',
      },
      {
        label: 'eXp Luxury',
        link: 'https://expluxury.com/?tl=en',
        target: 'blank',
      },
      {
        label: 'Accessibility',
      },
    ],
    column3: [
      {
        label: 'Terms and Conditions',
        link: 'https://expworldholdings.com/terms-of-service/',
        target: '_blank',
      },
      {
        label: 'Privacy Rights Request',
        link: 'https://submit-irm.trustarc.com/services/validation/e6d7516d-df9c-4404-bd9f-21a2f0d2a6a4/',
        target: '_blank',
      },
      {
        label: 'Privacy Policy',
        link: 'https://expworldholdings.com/privacy-policy/',
        target: '_blank',
      },
      {
        label: 'Cookie Policy',
        link: 'https://expworldholdings.com/cookie-policy/',
        target: '_blank',
      },
      {
        label: 'Brand Guidelines',
        link: 'https://join.exprealty.com/brand/',
        target: '_blank',
      },
      {
        label: 'Help',
        link: 'https://searchhelp.exprealty.com/hc/en-us/',
        target: '_blank',
      },
      {
        label: 'Sitemap',
        link: '/sitemap',
        isAnchor: true,
        target: '_blank',
      },
    ],
    bottom: [
      [
        {
          label: '© 2024 eXp Realty. eXp World Holdings, Inc. All Rights Reserved.',
        },
      ],
      [
        {
          label: ' ',
        },
      ],
      [
        {
          label: 'DMCA Notice',
          link: '/dmca',
          target: '_blank',
          isAnchor: true,
        },
      ],
      [
        {
          label: 'This site is protected by reCAPTCHA and the Google ',
        },
        {
          label: 'Privacy Policy ',
          link: 'https://policies.google.com/privacy',
          target: '_blank',
          isAnchor: true,
        },
        {
          label: 'and ',
        },
        {
          label: 'Terms of Service ',
          link: 'https://policies.google.com/terms',
          target: '_blank',
          isAnchor: true,
        },
        {
          label: 'apply.',
        },
      ],
      [
        {
          label: 'Fair Housing and Reasonable Accommodations',
          link: '/fair-housing-and-reasonable-accommodations',
          target: '_blank',
          isAnchor: true,
        },
      ],
      [
        {
          label: 'MLS Compliance Statements',
        },
      ],
      [
        {
          label: 'Karen Richards – Designated Managing Broker, Broker Operations – TX',
        },
      ],
      [
        {
          label: 'Texas Real Estate Commission information about brokerage services',
          link: 'https://dtzulyujzhqiu.cloudfront.net/expcorporate377/compliance/tx/entity/377_30621_iabs.pdf',
          isAnchor: true,
          target: '_blank',
        },
      ],
      [
        {
          label: 'Texas Real Estate Commission Consumer Protection Notice',
          link: 'https://www.trec.texas.gov/sites/default/files/pdf-forms/CN%201-4-1_1.pdf',
          isAnchor: true,
          target: '_blank',
        },
      ],
      [
        {
          label: 'eXp Realty® is committed to adhering to the guidelines of The New York State Fair '
          + 'Housing Regulations. ',
        },
        {
          label: 'The Fair Housing Notice',
          link: 'https://dos.ny.gov/fair-housing',
          isAnchor: true,
          target: '_blank',
        },
        {
          label: ' →',
          link: 'https://dos.ny.gov/system/files/documents/2021/08/fairhousingnotice.pdf',
          target: '_blank',
        },
      ],
      [
        {
          label: '*Standardized Operating Procedure for Purchasers of Real '
          + 'Estate Pursuant to Real Property Law 442-H. ',
        },
        {
          label: 'Learn More',
          link: 'https://drive.google.com/file/d/1o1iG6CO5hFgawNGJibmd34loBFJeIqqJ/view',
          isAnchor: true,
          target: '_blank',
        },
        {
          label: ' →',
          link: 'https://drive.google.com/file/d/1o1iG6CO5hFgawNGJibmd34loBFJeIqqJ/view',
          target: '_blank',
        },
      ],
      [
        {
          label: ' ',
        },
      ],
      [
        {
          label: 'eXp Realty of California, Inc. | CA DRE# 01878277 | Deborah Penny – Designated Broker',
        },
      ],
      [
        {
          label: 'eXp Realty of Southern California, Inc. | CA DRE# 02187306 | Gina Saad – Designated Broker',
        },
      ],
      [
        {
          label: 'eXp Realty of Greater Los Angeles, Inc. | CA DRE# 01240990 | Mike Mendibles – Designated Broker',
        },
      ],
      [
        {
          label: 'eXp Realty of Northern California, Inc. | CA DRE# 01951343 | Ryan Rosas – Designated Broker',
        },
      ],
    ],
    joinUsLink: 'https://join.exprealty.com',
    footerImageData: {
      src: '/next/assets/images/exp-footer-pic-us.jpg',
      alt: 'A group of people at an event are laughing and cheering, with one man enthusiastically greeting a seated couple in the front row.',
    },
  },
  searchOptions: [
    {
      type: 'Buy',
      placeholder(siteLocation, isOnMobile) {
        const neighbourhood = translate('neighbourhood', siteLocation);
        return isOnMobile ? `City, ${neighbourhood}, or address` : `Enter a city, ${neighbourhood}, address, MLS® number or school`;
      },
    },
    {
      type: 'Rent',
      placeholder(siteLocation, isOnMobile) {
        const neighbourhood = translate('neighbourhood', siteLocation);
        return isOnMobile ? `City, ${neighbourhood}, or address` : `Enter a city, ${neighbourhood}, address, MLS® number or school`;
      },
    },
    {
      type: 'Sold',
      placeholder(siteLocation, isOnMobile) {
        const neighbourhood = translate('neighbourhood', siteLocation);
        return isOnMobile ? `City, ${neighbourhood}, or address` : `Enter a city, ${neighbourhood}, address, MLS® number or school`;
      },
    },
    {
      type: 'Agents',
      placeholder(siteLocation) {
        const neighbourhood = translate('neighbourhood', siteLocation);
        return `Enter a city or ${neighbourhood}`;
      },
    },
    {
      type: 'HomeAppraisal',
      placeholder() {
        return 'Enter your home address';
      },
    },
  ],
  contentBlockBuy: {
    title: 'Buying your next home',
    copy: 'eXp Realty transforms home buying into a seamless experience, tailored to your needs with cutting-edge technology and expert support. Discover a better way to buy a home with eXp.',
    image: '/next/assets/images/content-block/exp-buy.jpg',
    link: '/buy',
  },
  contentBlockSell: {
    title: 'Selling your home',
    copy: 'Discover a smarter selling experience with eXp Realty with a simplified process that blends advanced technology and market insight to sell your home effectively and efficiently.',
    image: '/next/assets/images/content-block/exp-sell.jpg',
    link: '/sell',
  },
  privacyPolicy: (language?: SiteLanguage) => {
    switch (language) {
    case SiteLanguage.French:
      return 'https://expworldholdings.com/privacy-policy-french/';
    default:
      return 'https://expworldholdings.com/privacy-policy/';
    }
  },
  termsOfUse: (language?: SiteLanguage) => {
    switch (language) {
    case SiteLanguage.French:
      return 'https://expworldholdings.com/terms-of-service-french/';
    default:
      return 'https://expworldholdings.com/terms-of-service/';
    }
  },
  keywords: keywordsUS,
};

export default ExprealtyUsTheme;
